import { FC, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAcceptScheduledCallMutation,
  useGetCourseHearingQuery,
  teachurKeys,
  type CourseHearingListItem,
} from '@teachur/http-client';

import { Drawer } from '@new/common/ui/Drawer/Drawer';
import { Loader } from '@new/common/ui/Loader/Loader';
import { CourseHearingDrawerBody } from './CourseHearingDrawerBody';
import { CourseHearingDrawerHeader } from './CourseHearingDrawerHeader';
import { CourseHearingDrawerSidePanel } from './CourseHearingDrawerSidePanel';
import { useVideoCallContext } from '../../../../../contexts/VideoCallProvider';
import { getLatestHearingAttempt } from '../../utils/courseHearingHelpers';
import { CancelScheduledCallModal } from '@new/features/courses/components/CourseCompletionSteps/StepContent/HearingContent/CancelScheduledCallModal';

interface CourseHearingDrawerProps {
  selectedCall: CourseHearingListItem | null;
  setSelectedCall: (call: CourseHearingListItem | null) => void;
}

type ActiveSidePanel = 'submissionsHistory' | null;

export const CourseHearingDrawer: FC<CourseHearingDrawerProps> = ({
  selectedCall,
  setSelectedCall,
}) => {
  const [activeSidePanel, setActiveSidePanel] = useState<ActiveSidePanel>(null);

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const { openVideoCall } = useVideoCallContext();
  const queryClient = useQueryClient();

  const { data: freshCall, isLoading: isCallLoading } = useGetCourseHearingQuery(selectedCall?.id, {
    enabled: !!selectedCall?.id,
  });
  const currentCall = freshCall ?? null;
  const isDetailLoading = selectedCall !== null && isCallLoading;

  const latestAttempt = getLatestHearingAttempt(currentCall);

  const onAcceptSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: teachurKeys.courseHearings.all,
    });
  };

  const { mutate: acceptCall, isPending: isAccepting } = useAcceptScheduledCallMutation({
    onSuccess: onAcceptSuccess,
  });

  const handleAccept = () => {
    if (!latestAttempt) return;

    acceptCall(latestAttempt.id);
  };

  const handleCancel = () => {
    if (!latestAttempt) return;

    setIsCancelModalOpen(true);
  };

  const handleJoinCall = () => {
    if (!latestAttempt?.videoCallId) return;

    openVideoCall(latestAttempt.videoCallId);
    setSelectedCall(null);
  };

  const handleClose = () => {
    setIsCancelModalOpen(false);
    setSelectedCall(null);
    setActiveSidePanel(null);
  };

  const handleCancelModalClose = () => {
    setIsCancelModalOpen(false);
  };

  const onToggleSubmissionsHistory = () => {
    setActiveSidePanel((prev) => (prev === 'submissionsHistory' ? null : 'submissionsHistory'));
  };

  return (
    <Drawer
      open={selectedCall !== null}
      onClose={handleClose}
      header={
        <CourseHearingDrawerHeader
          call={currentCall}
          onClose={handleClose}
          onJoinCall={handleJoinCall}
          onAccept={handleAccept}
          onCancel={handleCancel}
          isAccepting={isAccepting}
          isCancelling={false}
          onOpenSubmissionsHistory={onToggleSubmissionsHistory}
        />
      }
      className="max-w-130!"
      sidePanel={
        <CourseHearingDrawerSidePanel
          call={currentCall}
          isSubmissionsHistoryOpen={activeSidePanel === 'submissionsHistory'}
          onCloseSubmissionsHistory={() => setActiveSidePanel(null)}
        />
      }
      isSidePanelOpen={activeSidePanel === 'submissionsHistory'}
    >
      {isDetailLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader />
        </div>
      ) : (
        <CourseHearingDrawerBody call={currentCall} />
      )}

      {currentCall && latestAttempt && (
        <CancelScheduledCallModal
          callId={latestAttempt.id}
          courseId={currentCall.courseId}
          isOpen={isCancelModalOpen}
          onClose={handleCancelModalClose}
          mode="instructor"
        />
      )}
    </Drawer>
  );
};
